import { InstructionOptionDTO, TravelCostProduct } from 'models/purchaseFlow'
import { PrestigeProductKinds, ProductKind } from 'constants/product'
import { PurchaseFlowProduct, PurchaseFlowProductOption, usePurchaseFlowProducts } from './PurchaseFlowProducts.context'
import { bigFromFee, percentageValue, valueAfterDiscount } from 'utils/price'
import { useEffect, useMemo } from 'react'

import Big from 'big.js'
import { InstructionType } from 'constants/purchaseFlow'
import constate from 'constate'
import { useBookCreative } from '../../common'
import { usePurchaseFlowConfig } from './PurchaseFlowConfig.context'
import { usePurchaseFlowOrderMeta } from './PurchaseFlowOrderMeta.context'

export interface InstructionCartItem extends InstructionOptionDTO {
  instructionProducts: TravelCostProduct[]
  type?: InstructionType
}

export interface InstructionExtraItem extends TravelCostProduct {
  kind: ProductKind
}

type PurchaseFlowCartProduct = {
  options: PurchaseFlowProductOption[]
} & PurchaseFlowProduct

type CartTotal = {
  /** The total amount after applying Value Added Tax (VAT). */
  totalAfterVat: Big
  /** The total amount before applying any discounts. */
  totalBeforeDiscount: Big
  /** The total amount after applying discounts. */
  totalAfterDiscount: Big
  /** The total amount of Value Added Tax (VAT). */
  totalVat: Big
  /** The total amount of discounts applied. */
  totalDiscount: Big
}

export const [CartContextProvider, useCart] = constate(() => {
  const { selectedCreative } = useBookCreative()
  const { catalogueVat, catalogueDiscount, extraServices, organizationInstructions, availablePaymentMethods } = usePurchaseFlowConfig()
  const { selectedProducts, selectedProductOptions } = usePurchaseFlowProducts()
  const { selectedMeetingType, selectedExtraServices, keyPickupTravelCostProduct, selectedPaymentMethod, updatePaymentMethod, setSelectedPaymentMethod, isNoPayment } = usePurchaseFlowOrderMeta()

  /** Sorted list of products and options marked as selected */
  const cartProductList = useMemo(() => {
    if (Object.values(selectedProducts).length === 0) return []

    const cartProducts: Record<number, PurchaseFlowCartProduct> = {}

    // Merges the product options according ID and sorts by price
    for (const productKey in selectedProducts) {
      if (selectedProductOptions.hasOwnProperty(productKey)) {

        const sortedProductOptions = Object.values(selectedProductOptions[productKey])
          .sort((optionA, optionB) => bigFromFee(optionA.feePrice).minus(bigFromFee(optionB.feePrice)).toNumber())

        cartProducts[productKey] = { ...selectedProducts[productKey], options: [...sortedProductOptions] }

      } else {
        cartProducts[productKey] = { ...selectedProducts[productKey], options: [] }
      }
    }

    return Object.values(cartProducts)
      .sort((prodA, prodB) => bigFromFee(prodA.feePrice).minus(bigFromFee(prodB.feePrice)).toNumber())
  }, [selectedProducts, selectedProductOptions])

  /** Total min and max duration of products of the order */
  const { sumMinTime, sumMaxTime } = useMemo(() => {
    let min = 0
    let max = 0

    for (const product of cartProductList) {
      min += product.timeOnSiteMin ?? 0
      max += product.timeOnSiteMax ?? 0

      min += product.options.reduce((sum, item) => sum + (item.timeOnSiteMin ?? 0), 0)
      max += product.options.reduce((sum, item) => sum + (item.timeOnSiteMax ?? 0), 0)
    }

    return {
      sumMinTime: min,
      sumMaxTime: max,
    }

  }, [cartProductList])

  /** Whether the order has a prestige product or not */
  const orderHasPrestigeProduct = useMemo(() => !!cartProductList.find((product) => PrestigeProductKinds.has(product.kind)), [cartProductList])

  /** List of selected instructions sent by BE (artificial ignored) */
  const cartInstructionList = useMemo(() => {
    const list: InstructionCartItem[] = []

    // HANDLE THE EXTRA SERVICES
    for (const serviceKind of selectedExtraServices) {
      const serviceInstruction = extraServices[serviceKind]

      if (!serviceInstruction) throw new Error(`Couldn't find an entry for ${serviceKind} in extraServices Record.`)

      const extraProducts: TravelCostProduct[] = []

      if (serviceKind === ProductKind.FAVOURITE_CREATIVE && selectedCreative?.travelCost) {
        extraProducts.push(selectedCreative.travelCost)
      }

      list.push({
        ...serviceInstruction,
        instructionProducts: extraProducts,
      })
    }

    // HANDLE MEETING INSTRUCTIONS
    if (selectedMeetingType) {
      const meetingInstruction = organizationInstructions[selectedMeetingType]
      const meetingProducts: TravelCostProduct[] = []

      if (selectedMeetingType === ProductKind.KEYS_PICKUP && keyPickupTravelCostProduct) {
        meetingProducts.push(keyPickupTravelCostProduct)
      }

      if (meetingInstruction) {
        list.push({
          ...meetingInstruction,
          instructionProducts: meetingProducts
        })
      }
    }

    // HANDLE PAYMENT METHOD
    if (selectedPaymentMethod) {
      const instruction = availablePaymentMethods[selectedPaymentMethod]

      if (instruction && bigFromFee(instruction.feePrice).gt(0)) {
        list.push({
          ...instruction,
          instructionProducts: []
        })
      }
    }

    return list
  }, [selectedMeetingType, selectedPaymentMethod, selectedExtraServices, extraServices, selectedCreative?.travelCost, organizationInstructions, keyPickupTravelCostProduct, availablePaymentMethods])

  /** Total price of all products and sub-products (options) */
  const totalProductsPrice = useMemo(() => {
    if (cartProductList.length === 0) return Big(0)

    return cartProductList.reduce((accumulator, product) => {
      let optionsPrice: Big = new Big(0)

      if (product.options && product.options.length > 0) {
        optionsPrice = product.options.reduce((optionsAccumulator, option) => {
          return optionsAccumulator.plus(bigFromFee(option.feePrice).times(option.quantity ?? 0))
        }, Big(0))
      }

      return accumulator.plus(bigFromFee(product.feePrice).times(product.quantity || 0)).plus(optionsPrice)
    }, Big(0))
  }, [cartProductList])

  /** Total price of all products, options and instructions marked as selected (no discounts applied) */
  const totalBeforeDiscount = useMemo(() => {

    const instructionPrice = cartInstructionList.reduce((accumulator, instructionOption) => {

      const instructionProductsPrice = instructionOption.instructionProducts.reduce((productsAcc, product) => productsAcc.plus(
        bigFromFee(product.productUnitPrice).times(product.productQuantity)
      ), Big(0))

      return accumulator.plus(bigFromFee(instructionOption.feePrice).plus(instructionProductsPrice))
    }, Big(0))

    return totalProductsPrice.plus(instructionPrice)
  }, [cartInstructionList, totalProductsPrice])

  const total: CartTotal = useMemo(() => {
    const totalAfterDiscount = valueAfterDiscount(totalBeforeDiscount, catalogueDiscount)
    const totalVat = percentageValue(totalAfterDiscount, (catalogueVat ?? Big(0)))

    return {
      totalVat,
      totalAfterDiscount,
      totalBeforeDiscount,
      totalAfterVat: totalAfterDiscount.plus(totalVat),
      totalDiscount: totalBeforeDiscount.minus(totalAfterDiscount)
    }
  }, [catalogueDiscount, catalogueVat, totalBeforeDiscount])

  // Check the payment method. When the cart is not empty, but it has 0 value, then remove the payment method
  useEffect(() => {
    if (cartProductList.length > 0 && total.totalAfterDiscount.eq(0)) {
      isNoPayment.current = true
      setSelectedPaymentMethod(null)
    } else {
      isNoPayment.current = false
      updatePaymentMethod()
    }
    // disable eslint warning for the following: setSelectedPaymentMethod, updatePaymentMethod
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartProductList.length, total.totalAfterDiscount])

  return {
    cartProductList,
    cartInstructionList,
    orderHasPrestigeProduct,
    sumMinTime,
    sumMaxTime,
    total,
  }
})
